/**
 * Global Streak Utilities
 * 
 * Functions for managing and calculating global streaks across ALL gameplay modes.
 * Streaks are universal - any level completion (course, just play, daily challenge, etc.)
 * counts toward the daily streak goal.
 * Uses UnifiedBatchSyncService for optimized Firestore writes.
 */

import { firestoreService } from '../services/database/FirestoreService';
import { batchSyncService } from '../services/UnifiedBatchSyncService';
import type {
  DailyActivity,
  GameplayMode,
  GlobalStreak,
  LevelActivity,
  StreakBadge
} from '../types/gamification';
import { STREAK_BADGES } from '../types/gamification';

const STORAGE_KEY_PREFIX = 'daily_activity_';

/**
 * Get today's date in YYYY-MM-DD format (local time)
 */
export function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Synchronous read of the localStorage cache. Internal helper for code that
 * already holds a reference (e.g. recordLevelCompletion mutating today's row).
 * External callers should use loadDailyActivities() which is Firestore-first.
 */
export function loadDailyActivitiesFromCache(userId: string): DailyActivity[] {
  try {
    const key = `${STORAGE_KEY_PREFIX}${userId}`;
    const data = localStorage.getItem(key);

    if (!data) {
      return [];
    }

    const activities: DailyActivity[] = JSON.parse(data);

    // Convert timestamp strings back to Date objects
    return activities.map(day => ({
      ...day,
      activities: day.activities.map(activity => ({
        ...activity,
        timestamp: new Date(activity.timestamp)
      }))
    }));
  } catch (error) {
    console.error('Error loading daily activities from cache:', error);
    return [];
  }
}

const BACKFILL_DONE_PREFIX = 'daily_activity_backfill_done_';

/**
 * Firestore-first read of daily activities. Cache-then-refresh when the local
 * cache is warm (returns immediately, refreshes in the background and dispatches
 * `dailyActivitiesSynced` so listeners can re-render). Cold-cache callers wait
 * for the round-trip so they don't render an empty streak.
 */
export async function loadDailyActivities(userId: string): Promise<DailyActivity[]> {
  const cached = loadDailyActivitiesFromCache(userId);

  if (cached.length === 0) {
    // Cold cache (new origin / new device). Block on Firestore so the UI
    // doesn't paint a 0-day streak before the data arrives.
    try {
      const remote = await firestoreService.getAllDailyActivities(userId);
      writeDailyActivitiesCache(userId, remote);
      return remote;
    } catch (error) {
      console.error('Cold-cache Firestore load failed, returning empty:', error);
      return cached;
    }
  }

  // Warm cache: return immediately, kick off a background refresh.
  void refreshDailyActivitiesFromFirestore(userId);
  return cached;
}

async function refreshDailyActivitiesFromFirestore(userId: string): Promise<void> {
  try {
    const remote = await firestoreService.getAllDailyActivities(userId);
    if (remote.length === 0) return;

    const local = loadDailyActivitiesFromCache(userId);
    const merged = mergeDailyActivities(local, remote);

    // Skip the cache write + event if nothing meaningfully changed.
    if (sameActivities(local, merged)) return;

    writeDailyActivitiesCache(userId, merged);
    window.dispatchEvent(new CustomEvent('dailyActivitiesSynced', {
      detail: { syncedCount: remote.length },
    }));
  } catch (error) {
    console.warn('Background daily-activity refresh failed:', error);
  }
}

// Merge two snapshots, most-recent-write-per-date wins (compare by totalLevels
// — higher count is the canonical authority). Same-count picks remote.
function mergeDailyActivities(local: DailyActivity[], remote: DailyActivity[]): DailyActivity[] {
  const byDate = new Map<string, DailyActivity>();
  for (const day of local) byDate.set(day.date, day);
  for (const day of remote) {
    const existing = byDate.get(day.date);
    if (!existing || day.totalLevels >= existing.totalLevels) {
      byDate.set(day.date, day);
    }
  }
  return Array.from(byDate.values()).sort((a, b) => b.date.localeCompare(a.date));
}

function sameActivities(a: DailyActivity[], b: DailyActivity[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].date !== b[i].date) return false;
    if (a[i].totalLevels !== b[i].totalLevels) return false;
  }
  return true;
}

function writeDailyActivitiesCache(userId: string, activities: DailyActivity[]): void {
  try {
    const key = `${STORAGE_KEY_PREFIX}${userId}`;
    localStorage.setItem(key, JSON.stringify(activities));
  } catch (error) {
    console.error('Error writing daily activities cache:', error);
  }
}

/**
 * Save daily activities to localStorage AND eagerly push the modified day to
 * Firestore so cross-origin reads see a current source of truth. Pass the date
 * that was just modified (callers always know — they wouldn't have mutated the
 * collection otherwise).
 */
function saveDailyActivities(
  userId: string,
  activities: DailyActivity[],
  modifiedDate: string,
): void {
  writeDailyActivitiesCache(userId, activities);

  // Eager Firestore write for the changed day (fire-and-forget). This replaces
  // the previous "today-only via 5s debounced batch" path that left historical
  // days unsynced. The batch sync queue is left in place as belt-and-suspenders.
  const day = activities.find(a => a.date === modifiedDate);
  if (day) {
    firestoreService.saveDailyActivity(userId, day).catch(error => {
      console.warn(`Eager Firestore write for ${modifiedDate} failed (will retry via batch):`, error);
    });
  }

  batchSyncService.queueDailyActivityUpdate(userId);
}

/**
 * One-time backfill: push every locally-cached day that's not already on
 * Firestore (or has more activity than Firestore) up to the cloud. Idempotent
 * via a per-user localStorage marker. Run once on login of a device that
 * predates the eager-write architecture.
 */
export async function backfillDailyActivitiesToFirestore(userId: string): Promise<void> {
  const doneKey = `${BACKFILL_DONE_PREFIX}${userId}`;
  if (localStorage.getItem(doneKey) === '1') return;

  const local = loadDailyActivitiesFromCache(userId);
  if (local.length === 0) {
    localStorage.setItem(doneKey, '1');
    return;
  }

  try {
    const remote = await firestoreService.getAllDailyActivities(userId);
    const remoteByDate = new Map(remote.map(d => [d.date, d]));

    const toWrite = local.filter(localDay => {
      const remoteDay = remoteByDate.get(localDay.date);
      return !remoteDay || localDay.totalLevels > remoteDay.totalLevels;
    });

    if (toWrite.length === 0) {
      localStorage.setItem(doneKey, '1');
      return;
    }

    console.log(`📤 Backfilling ${toWrite.length} day(s) of activity to Firestore...`);
    await Promise.all(toWrite.map(day => firestoreService.saveDailyActivity(userId, day)));
    localStorage.setItem(doneKey, '1');
    console.log(`✅ Backfill complete (${toWrite.length} days)`);
  } catch (error) {
    // Don't mark done — try again on next login.
    console.error('Daily-activity backfill failed (will retry next login):', error);
  }
}

/**
 * Record a level completion (universal function for ALL modes)
 */
export function recordLevelCompletion(
  userId: string,
  levelId: string,
  mode: GameplayMode,
  metadata?: any
): void {
  const today = getTodayDateString();
  // Synchronous read from local cache: this function is called from hot paths
  // (every level completion) and we can't afford a Firestore round-trip here.
  // The eager dual-write below keeps Firestore current.
  const activities = loadDailyActivitiesFromCache(userId);
  
  // Find or create today's activity entry
  let todayActivity = activities.find(a => a.date === today);
  
  if (!todayActivity) {
    todayActivity = {
      date: today,
      activities: [],
      totalLevels: 0,
      modeBreakdown: []
    };
    activities.push(todayActivity);
  }
  
  // Add the new level completion
  const newActivity: LevelActivity = {
    levelId,
    mode,
    timestamp: new Date(),
    metadata
  };
  
  todayActivity.activities.push(newActivity);
  todayActivity.totalLevels = todayActivity.activities.length;
  
  // Update mode breakdown
  const modeBreakdown = new Map<GameplayMode, number>();
  todayActivity.activities.forEach(activity => {
    modeBreakdown.set(activity.mode, (modeBreakdown.get(activity.mode) || 0) + 1);
  });
  
  todayActivity.modeBreakdown = Array.from(modeBreakdown.entries()).map(([mode, count]) => ({
    mode,
    count
  }));
  
  // Sort activities by date (most recent first) and keep last 365 days
  activities.sort((a, b) => b.date.localeCompare(a.date));
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - 365);
  // Use local timezone for consistency
  const cutoffString = `${cutoffDate.getFullYear()}-${String(cutoffDate.getMonth() + 1).padStart(2, '0')}-${String(cutoffDate.getDate()).padStart(2, '0')}`;
  
  const recentActivities = activities.filter(a => a.date >= cutoffString);

  saveDailyActivities(userId, recentActivities, today);
  
  // NOTE: Aggregations are now handled by the new AggregationService
  // which updates instantly (0ms) on level completion via LevelCompletionService
}

/**
 * Calculate global streak across all gameplay modes
 */
export function calculateGlobalStreak(
  dailyActivities: DailyActivity[],
  requiredLevels: number,
  savedDays?: Set<string>
): GlobalStreak {
  if (dailyActivities.length === 0 && (!savedDays || savedDays.size === 0)) {
    return {
      consecutiveDays: 0,
      todayGoalMet: false,
      todayLevelsCompleted: 0,
      requiredLevels,
      longestStreak: 0
    };
  }

  const isCovered = (dateStr: string, activity: DailyActivity | undefined): boolean => {
    if (activity && activity.totalLevels >= requiredLevels) return true;
    return savedDays?.has(dateStr) ?? false;
  };

  const today = getTodayDateString();
  const sortedDays = [...dailyActivities].sort((a, b) => b.date.localeCompare(a.date));

  // Check today's progress
  const todayActivity = sortedDays.find(a => a.date === today);
  const todayLevelsCompleted = todayActivity?.totalLevels || 0;
  const todayGoalMet = todayLevelsCompleted >= requiredLevels;

  // Calculate current streak
  let consecutiveDays = 0;
  const checkDate = new Date();

  // If today's goal is met, count today
  if (todayGoalMet) {
    consecutiveDays = 1;
    checkDate.setDate(checkDate.getDate() - 1);
  } else {
    // If today's goal not met, check from yesterday
    checkDate.setDate(checkDate.getDate() - 1);
  }

  // Count consecutive days backwards
  while (true) {
    // Use local timezone for consistency
    const dateString = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
    const dayActivity = sortedDays.find(a => a.date === dateString);

    if (isCovered(dateString, dayActivity)) {
      consecutiveDays++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }
  
  // Calculate longest streak — build the set of covered dates (activity meets
  // goal OR savedDays includes it), then walk chronologically.
  const coveredDates = new Set<string>();
  for (const day of dailyActivities) {
    if (day.totalLevels >= requiredLevels) coveredDates.add(day.date);
  }
  if (savedDays) {
    for (const d of savedDays) coveredDates.add(d);
  }
  const sortedCovered = Array.from(coveredDates).sort();

  const areConsecutiveDays = (date1: string, date2: string): boolean => {
    const d1 = new Date(date1 + 'T12:00:00');
    const d2 = new Date(date2 + 'T12:00:00');
    const diffMs = d2.getTime() - d1.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return diffDays === 1;
  };

  let longestStreak = 0;
  let currentStreakCount = 0;
  let lastDateStr: string | null = null;

  for (const dateStr of sortedCovered) {
    if (lastDateStr && areConsecutiveDays(lastDateStr, dateStr)) {
      currentStreakCount++;
    } else {
      longestStreak = Math.max(longestStreak, currentStreakCount);
      currentStreakCount = 1;
    }
    lastDateStr = dateStr;
  }

  longestStreak = Math.max(longestStreak, currentStreakCount, consecutiveDays);
  
  return {
    consecutiveDays,
    todayGoalMet,
    todayLevelsCompleted,
    requiredLevels,
    longestStreak
  };
}

/**
 * Get today's progress breakdown by mode
 */
export function getTodayProgress(dailyActivities: DailyActivity[]): { mode: GameplayMode; count: number }[] {
  const today = getTodayDateString();
  const todayActivity = dailyActivities.find(a => a.date === today);
  
  if (!todayActivity) {
    return [];
  }
  
  return todayActivity.modeBreakdown;
}

/**
 * Check if a specific day has streak completion
 */
export function isStreakDayComplete(
  date: string,
  dailyActivities: DailyActivity[],
  requiredLevels: number
): boolean {
  const dayActivity = dailyActivities.find(a => a.date === date);
  return (dayActivity?.totalLevels || 0) >= requiredLevels;
}

/**
 * Get the streak badge for a given consecutive days count
 */
export function getStreakBadge(consecutiveDays: number): StreakBadge | null {
  // Find the appropriate badge
  for (let i = STREAK_BADGES.length - 1; i >= 0; i--) {
    const badge = STREAK_BADGES[i];
    if (consecutiveDays >= badge.minDays) {
      return badge;
    }
  }
  
  // No badge earned yet (less than minimum required days)
  return null;
}

/**
 * Get total levels completed for a specific date (across all modes)
 */
export function getLevelsCompletedOnDate(
  date: string,
  dailyActivities: DailyActivity[]
): number {
  const dayActivity = dailyActivities.find(a => a.date === date);
  return dayActivity?.totalLevels || 0;
}

/**
 * Get mode breakdown for a specific date
 */
export function getModeBreakdownForDate(
  date: string,
  dailyActivities: DailyActivity[]
): { mode: GameplayMode; count: number }[] {
  const dayActivity = dailyActivities.find(a => a.date === date);
  return dayActivity?.modeBreakdown || [];
}

/**
 * Get today's training session breakdown (by language + keyboard)
 * Returns the count of levels completed per training session today
 */
export function getTodaySessionBreakdown(
  dailyActivities: DailyActivity[]
): Array<{ language: string; keyboardId: string; count: number; icon: string }> {
  const today = getTodayDateString();
  const todayActivity = dailyActivities.find(a => a.date === today);
  
  if (!todayActivity) {
    console.log('📊 getTodaySessionBreakdown: No activity for today');
    return [];
  }
  
  console.log(`📊 getTodaySessionBreakdown: Found ${todayActivity.activities.length} total activities today`);
  
  // Group course activities by language + keyboardId
  const sessionMap = new Map<string, { language: string; keyboardId: string; count: number }>();
  
  todayActivity.activities.forEach((activity, idx) => {
    console.log(`  Activity ${idx + 1}:`, {
      mode: activity.mode,
      levelId: activity.levelId,
      language: activity.metadata?.language,
      keyboardId: activity.metadata?.keyboardId
    });
    
    // Only count 'course' mode activities (training sessions)
    if (activity.mode === 'course' && activity.metadata?.language && activity.metadata?.keyboardId) {
      const key = `${activity.metadata.language}_${activity.metadata.keyboardId}`;
      
      if (sessionMap.has(key)) {
        sessionMap.get(key)!.count++;
      } else {
        sessionMap.set(key, {
          language: activity.metadata.language,
          keyboardId: activity.metadata.keyboardId,
          count: 1
        });
      }
    }
  });
  
  const result = Array.from(sessionMap.values()).map(session => ({
    ...session,
    icon: (session.language === 'basics' || session.language === 'english') ? '📝' : '🐍'
  }));
  
  console.log('📊 getTodaySessionBreakdown result:', result);
  console.log(`📊 Total levels from sessions: ${result.reduce((sum, s) => sum + s.count, 0)}`);
  console.log(`📊 Total levels from DailyActivity: ${todayActivity.totalLevels}`);
  
  return result;
}

/**
 * Sync daily activities FROM Firestore to localStorage
 * Called when user logs in - ALWAYS runs to ensure cross-browser sync
 * Merges activities properly, combining unique activities from both sources
 */
export async function syncDailyActivitiesFromFirestore(userId: string): Promise<number> {
  try {
    console.log('📅 syncDailyActivitiesFromFirestore: Starting for user', userId);
    const firestoreActivities = await firestoreService.getAllDailyActivities(userId);
    
    if (firestoreActivities.length === 0) {
      console.log('📅 No daily activities found in Firestore');
      return 0;
    }
    
    console.log(`📅 Got ${firestoreActivities.length} activities from Firestore:`, 
      firestoreActivities.map(a => `${a.date}: ${a.totalLevels} levels`).slice(0, 5));
    
    const localActivities = loadDailyActivitiesFromCache(userId);
    console.log(`📅 Local activities: ${localActivities.length} days`,
      localActivities.map(a => `${a.date}: ${a.totalLevels} levels`).slice(0, 5));
    
    // Merge: combine activities from both sources
    const mergedActivities = [...localActivities];
    let syncedCount = 0;
    
    for (const fsActivity of firestoreActivities) {
      const existingIndex = mergedActivities.findIndex(a => a.date === fsActivity.date);
      
      if (existingIndex === -1) {
        // Date doesn't exist locally, add it
        mergedActivities.push(fsActivity);
        syncedCount++;
      } else {
        // Date exists in both - merge the activities
        const localDay = mergedActivities[existingIndex];
        
        // If Firestore has more levels, use Firestore data
        // (it likely has activities from other browsers)
        if (fsActivity.totalLevels > localDay.totalLevels) {
          mergedActivities[existingIndex] = fsActivity;
          syncedCount++;
          console.log(`📅 Merged ${fsActivity.date}: Firestore has ${fsActivity.totalLevels} levels vs local ${localDay.totalLevels}`);
        } else if (localDay.totalLevels > fsActivity.totalLevels) {
          // Local has more - push to Firestore to sync up
          console.log(`📅 Local has more for ${localDay.date}: ${localDay.totalLevels} vs Firestore ${fsActivity.totalLevels}, syncing UP...`);
          firestoreService.saveDailyActivity(userId, localDay).catch(err => 
            console.error('Error syncing daily activity UP:', err)
          );
        }
      }
    }
    
    // Also check if local has dates that Firestore doesn't have - sync them up
    const today = getTodayDateString();
    for (const localActivity of localActivities) {
      const existsInFirestore = firestoreActivities.some(a => a.date === localActivity.date);
      const fsActivity = firestoreActivities.find(a => a.date === localActivity.date);
      
      if (!existsInFirestore && localActivity.totalLevels > 0) {
        console.log(`📅 Local activity for ${localActivity.date} not in Firestore, syncing UP...`);
        await firestoreService.saveDailyActivity(userId, localActivity);
        console.log(`☁️ Pushed ${localActivity.date} (${localActivity.totalLevels} levels) to Firestore`);
      } else if (fsActivity && localActivity.totalLevels > fsActivity.totalLevels) {
        // Local has more levels for this date - push up
        console.log(`📅 Local has more for ${localActivity.date}: ${localActivity.totalLevels} vs Firestore ${fsActivity.totalLevels}, syncing UP...`);
        await firestoreService.saveDailyActivity(userId, localActivity);
        console.log(`☁️ Pushed ${localActivity.date} (${localActivity.totalLevels} levels) to Firestore`);
      }
    }
    
    // Special case: always sync today's local activity if it exists
    const todayLocal = localActivities.find(a => a.date === today);
    const todayFirestore = firestoreActivities.find(a => a.date === today);
    if (todayLocal && todayLocal.totalLevels > 0) {
      if (!todayFirestore || todayLocal.totalLevels > todayFirestore.totalLevels) {
        console.log(`📅 Forcing sync of today's activity: ${todayLocal.totalLevels} levels`);
        await firestoreService.saveDailyActivity(userId, todayLocal);
        console.log(`☁️ Pushed today's activity to Firestore`);
      }
    }
    
    // Sort by date
    mergedActivities.sort((a, b) => b.date.localeCompare(a.date));
    
    // Save to localStorage (don't re-sync to Firestore)
    // First, aggressively clear space
    const key = `${STORAGE_KEY_PREFIX}${userId}`;
    
    // Clear ALL non-essential data to make room
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) {
        // Remove other users' data
        if (k.startsWith('daily_activity_') && k !== key) {
          keysToRemove.push(k);
        }
        // Remove old aggregation data from other users
        if (k.startsWith('agg_') && !k.includes(userId)) {
          keysToRemove.push(k);
        }
        // Remove old level completion cache
        if (k.startsWith('level_completion_') && !k.includes(userId)) {
          keysToRemove.push(k);
        }
        // Remove old progress data
        if ((k.startsWith('basicsProgress_') || k.startsWith('pythonProgress_')) && !k.includes(userId)) {
          keysToRemove.push(k);
        }
        // Remove old keyboard data from other users
        if (k.startsWith('keyboard_') && !k.includes(userId)) {
          keysToRemove.push(k);
        }
        // Remove old points data from other users
        if (k.startsWith('user_points_') && !k.includes(userId)) {
          keysToRemove.push(k);
        }
      }
    }
    
    if (keysToRemove.length > 0) {
      console.log(`🧹 Clearing ${keysToRemove.length} old localStorage items to make room...`);
      keysToRemove.forEach(k => localStorage.removeItem(k));
    }
    
    // Helper to clear user's own old data if needed
    const clearOwnOldData = () => {
      console.log('🧹 Clearing user\'s own old data to make room...');
      // Clear old aggregations (keep only recent ones)
      const aggKeysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(`agg_${userId}_`)) {
          aggKeysToRemove.push(k);
        }
      }
      // Remove half of them
      aggKeysToRemove.slice(0, Math.floor(aggKeysToRemove.length / 2)).forEach(k => localStorage.removeItem(k));
      console.log(`🧹 Removed ${Math.floor(aggKeysToRemove.length / 2)} old aggregation entries`);
    };

    try {
      // Limit to last 14 days to avoid quota issues
      const limitedActivities = mergedActivities.slice(0, 14);
      localStorage.setItem(key, JSON.stringify(limitedActivities));
      console.log(`📅 Synced ${syncedCount} daily activities from Firestore (kept last ${limitedActivities.length} days)`);
    } catch (storageError) {
      if (storageError instanceof Error && storageError.name === 'QuotaExceededError') {
        console.warn('⚠️ localStorage quota exceeded, clearing more data...');
        clearOwnOldData();
        try {
          // Keep only last 7 days
          const limitedActivities = mergedActivities.slice(0, 7);
          localStorage.setItem(key, JSON.stringify(limitedActivities));
          console.log(`📅 Saved last 7 days after clearing space`);
        } catch {
          console.error('❌ Still quota exceeded, trying just today...');
          // Last resort: save just today
          try {
            const todayData = mergedActivities.find(a => a.date === getTodayDateString());
            if (todayData) {
              localStorage.setItem(key, JSON.stringify([todayData]));
              console.log(`📅 Saved only today's activity`);
            }
          } catch {
            console.error('❌ localStorage completely full - cannot save daily activities');
            // Ultimate fallback: just don't save to localStorage, the data is still in Firestore
            console.log('⚠️ Daily activities exist in Firestore but cannot be cached locally');
          }
        }
      }
    }
    
    return syncedCount;
  } catch (error) {
    console.error('Error syncing daily activities from Firestore:', error);
    return 0;
  }
}

/**
 * Check if user has any local daily activities
 */
export function hasLocalDailyActivities(userId: string): boolean {
  const key = `${STORAGE_KEY_PREFIX}${userId}`;
  const data = localStorage.getItem(key);
  if (!data) return false;
  
  try {
    const activities = JSON.parse(data);
    return Array.isArray(activities) && activities.length > 0;
  } catch {
    return false;
  }
}

